<template>
  <div class="question-container bg-secondary-3">
    <div
      v-for="(question, questionIndex) in questionsList"
      :key="questionIndex"
      class="question-item row mx-auto col px-0"
    >
      <div
        class="question-box-content row mx-auto col-12 d-flex justify-content-between align-items-end px-0"
      >
        <p class="col px-0 color-gray-6">
          {{ question.description }}
        </p>
        <div
          class="custom-radio-container d-flex align-items-end px-0"
          :class="{
            col: !removeColHeader,
          }"
        >
          <div
            v-for="(answer, answerIndex) in question.possibleAnswers"
            :id="`rbAnswersContainer-${answerIndex}`"
            :key="answerIndex"
            class="text-center rounded-top mb-0 custom-radio-button"
            :class="{
              'bg-primary-1': question.submittedAnswerTypeId === answer.id,
              'bg-secondary-9': question.submittedAnswerTypeId !== answer.id,
              'bg-red': false,
            }"
          >
            <div :id="`radio-answer-${answerIndex}`" class="radio">
              <label
                :id="`lbl-radio-answer-${answerIndex}`"
                class="radio-label w-100 mb-0"
              >
                <input
                  :id="`radio-button-answer-${answerIndex}`"
                  type="radio"
                  :name="`radio-answer-YesNoOption`"
                  :value="question.submittedAnswerTypeId === answer.id"
                  :disabled="isReadonly"
                  @change="updateSelectedAnswer(question.id, answer.id)"
                />
                <span
                  :id="`span-icon-${answerIndex}`"
                  class="cr rounded-circle"
                  :class="{
                    'cr-inactive': question.submittedAnswerTypeId !== answer.id,
                    'cr-active': question.submittedAnswerTypeId === answer.id,
                    'cr-error': false,
                  }"
                >
                  <i
                    :id="`answer-icon-${answerIndex}`"
                    class="cr-icon fa fa-check text-white"
                    :class="{
                      'show-check':
                        question.submittedAnswerTypeId === answer.id,
                    }"
                  />
                </span>
              </label>
            </div>
            <span
              class="step-label font-12"
              :class="{
                'color-gray-8': question.submittedAnswerTypeId !== answer.id,
                'color-white fw-bold':
                  question.submittedAnswerTypeId === answer.id,
              }"
            >
              {{ answer.name }}
            </span>
          </div>
        </div>
      </div>
      <Error
        v-if="validate && question.submittedAnswerTypeId == null"
        :is-alert-mode="false"
        class="mt-0"
        variant="no-border"
        message="Favor de seleccionar Sí o No"
      />
      <div
        v-if="studentHasReferralForSocioEmotionalAssistant(question)"
        class="question-document bg-secondary-9 rounded col-12 p-0"
      >
        <div
          v-if="question.uploadDocumentPanel"
          class="document-uploader mx-auto row bg-white rounded py-3 px-4"
        >
          <div class="col">
            <label class="field-label">Documento</label>
            <div class="field-value">
              Referido para asistencia
            </div>
          </div>
          <div class="col">
            <label class="field-label">Archivo</label>
            <div class="field-value">
              {{ displayDocumentName(question) | empty }}
            </div>
          </div>
          <div class="col d-flex justify-content-end align-items-center">
            <custom-file-input
              id="question-document-upload"
              ref="question-document-upload"
              display-style="normal"
              :accept="'.pdf'"
              :attachment-id="documentAttachmentId(question)"
              :on-download="onDownloadDocument"
              :disabled="!isDraft"
              @input="(file) => onUploadFile(question.id, file)"
            />
          </div>
        </div>
        <BoxIconButton
          v-else
          icon="fas fa-file-plus"
          title="Oprima aquí para añadir documento Referido para asistencia"
          class="box-icon-button"
          @click="onAddClickHandler(question)"
        />
        <Error
          v-if="!displayDocumentName(question) && validate"
          :is-alert-mode="false"
          class="mt-0"
          variant="no-border"
          message="Favor de adjuntar el documento Referido para asistencia."
        />
      </div>
      <div
        v-if="studentConductWereAnalizedBeforeFBA(question)"
        class="question-document bg-secondary-9 rounded col-12 p-0"
      >
        <div class="document-uploader mx-auto row bg-white rounded py-3 px-4">
          <div class="col">
            <label class="field-label">Tipo de Documento</label>
            <div class="field-value">
              SAEE-14 Evaluación Funcional de la Conducta
            </div>
          </div>
          <div class="col">
            <label class="field-label">Archivo</label>
            <div class="field-value">
              <span v-if="haveDocumentReadonly">
                {{ documentReadonly.fileName }}
              </span>
              <span v-else>
                {{
                  (getDocumentSAEE14Model.name || getDocumentSAEE14.fileName)
                    | empty
                }}
              </span>
            </div>
          </div>
          <div class="col d-flex justify-content-end align-items-center">
            <custom-file-input
              v-if="haveDocumentReadonly"
              id="question-document-readonly"
              display-style="normal"
              :accept="'.pdf'"
              :attachment-id="documentReadonly.fileId"
              :on-download="onDownloadDocument"
              disabled
            />
            <custom-file-input
              v-else
              id="question-document-upload"
              ref="question-document-upload"
              display-style="normal"
              :accept="'.pdf'"
              :attachment-id="getDocumentSAEE14.itemAttachmentId"
              :on-download="onDownloadDocument"
              :disabled="!isDraft"
              @input="onUploadSAEE14File"
            />
          </div>
        </div>
        <Error
          v-if="showSaee14DocumentValidationError"
          :is-alert-mode="false"
          class="mt-0"
          variant="no-border"
          message="Favor de anejar el documento SAEE-14 Evaluación Funcional de la Conducta"
        />
      </div>
    </div>

    <div
      v-for="(question, questionIndex) in bottomQuestions"
      :key="questionIndex"
      class="question-item row mx-auto col px-0"
    >
      <div
        class="question-box-content row mx-auto col-12 d-flex justify-content-between align-items-end px-0"
      >
        <p class="col px-0 color-gray-6">
          {{ question.description }}
        </p>
        <div
          class="custom-radio-container d-flex align-items-end px-0"
          :class="{
            col: !removeColHeader,
          }"
        >
          <div
            v-for="(answer, answerIndex) in question.possibleAnswers"
            :id="`rbAnswersContainer-${answerIndex}`"
            :key="answerIndex"
            class="text-center rounded-top mb-0 custom-radio-button"
            :class="{
              'bg-primary-1': question.submittedAnswerTypeId === answer.id,
              'bg-secondary-9': question.submittedAnswerTypeId !== answer.id,
              'bg-red': false,
            }"
          >
            <div :id="`radio-answer-${answerIndex}`" class="radio">
              <label
                :id="`lbl-radio-answer-${answerIndex}`"
                class="radio-label w-100 mb-0"
              >
                <input
                  :id="`radio-button-answer-${answerIndex}`"
                  type="radio"
                  :name="`radio-answer-YesNoOption`"
                  :value="question.submittedAnswerTypeId === answer.id"
                  :disabled="isReadonly"
                  @change="updateSelectedAnswer(question.id, answer.id)"
                />
                <span
                  :id="`span-icon-${answerIndex}`"
                  class="cr rounded-circle"
                  :class="{
                    'cr-inactive': question.submittedAnswerTypeId !== answer.id,
                    'cr-active': question.submittedAnswerTypeId === answer.id,
                    'cr-error': false,
                  }"
                >
                  <i
                    :id="`answer-icon-${answerIndex}`"
                    class="cr-icon fa fa-check text-white"
                    :class="{
                      'show-check':
                        question.submittedAnswerTypeId === answer.id,
                    }"
                  />
                </span>
              </label>
            </div>
            <span
              class="step-label font-12"
              :class="{
                'color-gray-8': question.submittedAnswerTypeId !== answer.id,
                'color-white fw-bold':
                  question.submittedAnswerTypeId === answer.id,
              }"
            >
              {{ answer.name }}
            </span>
          </div>
        </div>
      </div>
      <Error
        v-if="validate && question.submittedAnswerTypeId == null"
        :is-alert-mode="false"
        class="mt-0"
        variant="no-border"
        message="Favor de seleccionar Sí o No"
      />
    </div>

    <div class="d-flex justify-content-end">
      <action-button
        v-if="isDraft"
        type="button"
        text="Guardar"
        i="fa fa-save pr-2"
        variant="success"
        @click="onSaveQuestions"
      />
    </div>
  </div>
</template>

<script>
import store from '@/store/index';
import peiCode from '@/utils/constants/pei/peiCode';
import AnswerType from '@/utils/constants/pei/serviceNeedQuestionnaire/answerType';
import needsService from '@/services/pei/serviceNeedQuestionnaire';
import studentService from '@/services/student.js';
import ActionButton from 'ActionButton';
import BoxIconButton from '@/components/pei/shared/BoxIconButton';
import CustomFileInput from 'CustomFileInput';
import documentsService from '@/services/documents';
import Error from '@/components/common/Error';
import peiStatus from '@/utils/constants/pei/peiStatus';
import StudentBehaviourAlternatives from '@/utils/constants/pei/inappropiateConductAlternatives.js';
import documentTypes from '@/utils/constants/documentTypes.js';
import objectToFormData from 'object-to-formdata';
import mixAlert from '@/mixin/alert.mixin';

const Questions = {
  DoesStudentHaveInterventionPlan: 16,
  WereStudentConductsAnalyzedAfterFBA: 17,
  DoesStudentHaveReferralForSocioEmotionalAssistance: 18,
  DoesStudentHaveSignificantRisks: 23,
};

export default {
  name: 'QuestionsConductualSupport',
  mixins: [mixAlert],
  components: {
    ActionButton,
    BoxIconButton,
    CustomFileInput,
    Error,
  },
  props: {
    peiId: {
      type: String,
      default: () => '',
    },
    isReadonly: {
      type: Boolean,
      default: () => false,
    },
    removeColHeader: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: () => false,
    },
    inappropriateConductAlternative: {
      type: Number,
      default: 0,
      required: true,
    },
    studentSieId: {
      type: String,
      default: () => '',
    },
  },
  data() {
    return {
      questions: [],
      bottomQuestions: [],
      documentSAEE14: null,
      documentSAEE14Model: null,
      documentReadonly: {
        fileId: null,
        fileName: null,
      },
      haveDocumentReadonly: false,
    };
  },
  async mounted() {
    if (!this.questions?.length) {
      await this.getQuestions();
    }
    await this.populateDocument();
  },
  computed: {
    validationTriggered() {
      return store.getters.getPeiValidationTriggered;
    },
    isCompleteValidationActive() {
      return this.validate;
    },
    showSaee14DocumentValidationError() {
      const showError =
        !this.haveDocumentReadonly &&
        (this.getDocumentSAEE14.fileName == null ||
          this.getDocumentSAEE14.fileName == '') &&
        this.isCompleteValidationActive;

      if (
        showError &&
        this.validationTriggered.validation &&
        this.validationTriggered.hasErrors('Tab6', 'NeedGuide-Saee14-doc')
      ) {
        return true;
      }

      return false;
    },
    selectedPeiId() {
      return store.getters.SelectedPei;
    },
    selectedStudent() {
      return store.getters.SelectedStudent;
    },
    questionsList() {
      let _questions = [...this.questions];

      const studentHasInterventionPlanQuestion = this.questions.find(
        (x) => x.id === Questions.DoesStudentHaveInterventionPlan
      );
      if (
        studentHasInterventionPlanQuestion?.submittedAnswerTypeId !==
        AnswerType.No
      ) {
        _questions = _questions.filter(
          (x) =>
            x.id !==
            Questions.DoesStudentHaveReferralForSocioEmotionalAssistance
        );
      }

      const studentHasReferralForSocioEmotionalAssistantQuestion = this.questions.find(
        (x) =>
          x.id === Questions.DoesStudentHaveReferralForSocioEmotionalAssistance
      );
      const displayStudentConductsWereAnalyzedFBA =
        studentHasInterventionPlanQuestion?.submittedAnswerTypeId ===
          AnswerType.Yes ||
        (studentHasInterventionPlanQuestion?.submittedAnswerTypeId ===
          AnswerType.No &&
          studentHasReferralForSocioEmotionalAssistantQuestion?.submittedAnswerTypeId ===
            AnswerType.Yes);

      if (!displayStudentConductsWereAnalyzedFBA) {
        _questions = _questions.filter(
          (x) => x.id !== Questions.WereStudentConductsAnalyzedAfterFBA
        );
      }

      return _questions.sort((a, b) => {
        if (a.id === Questions.WereStudentConductsAnalyzedAfterFBA) return 1;
        if (b.id === Questions.WereStudentConductsAnalyzedAfterFBA) return -1;
        return a.id - b.id;
      });
    },
    studentInterventionPlanHasValue() {
      return (questionItem) =>
        questionItem.id === Questions.DoesStudentHaveInterventionPlan &&
        !!questionItem.submittedAnswerTypeId;
    },
    studentHasReferralForSocioEmotionalAssistant() {
      return (questionItem) =>
        questionItem.id ===
          Questions.DoesStudentHaveReferralForSocioEmotionalAssistance &&
        questionItem.submittedAnswerTypeId === AnswerType.Yes;
    },
    studentConductWereAnalizedBeforeFBA() {
      return (questionItem) =>
        questionItem.id === Questions.WereStudentConductsAnalyzedAfterFBA &&
        questionItem.submittedAnswerTypeId === AnswerType.Yes;
    },
    displayDocumentName() {
      return (questionItem) =>
        questionItem.documentModel?.name ||
        questionItem.submittedDocument?.filename;
    },
    getDocumentSAEE14() {
      return this.documentSAEE14 || {};
    },
    getDocumentSAEE14Model() {
      return this.documentSAEE14Model || {};
    },
    documentAttachmentId() {
      return (questionItem) => {
        if (questionItem.documentModel) {
          return null;
        }
        return questionItem.submittedDocument?.documentId;
      };
    },
    isDraft() {
      return store.getters.SelectedPeiStatus == peiStatus.Borrador;
    },
  },
  methods: {
    resetQuestionState(questionItem) {
      if (!questionItem) return;

      questionItem.submittedAnswerTypeId = null;
      questionItem.submittedComment = null;
      questionItem.documentModel = null;
      questionItem.uploadDocumentPanel = false;
      questionItem.submittedDocument = null;
    },
    resetHiddenQuestionsBeforeSave() {
      const visibleQuestionIds = this.questionsList.map((questionItem) =>
        questionItem.id
      );

      this.questions.forEach((questionItem) => {
        if (!visibleQuestionIds.includes(questionItem.id)) {
          this.resetQuestionState(questionItem);
        }
      });
    },
    async populateDocument() {
      const { data } = await studentService.getConductalPlanReadonlyDocument(
        this.selectedPeiId
      );
      this.haveDocumentReadonly = !!data.id;

      if (this.haveDocumentReadonly) {
        this.documentReadonly = {
          fileId: data.id,
          fileName: data.name,
        };
        return;
      }

      await this.getSAEE14BehavioralDocument();
    },
    async getQuestions() {
      const _questions = await needsService.getQuestions({
        peiId: this.selectedPeiId ?? this.peiId,
        peiCodeId: peiCode.SaludConductal,
      });

      const questions = _questions.map((questionItem) => ({
        ...questionItem,
        documentModel: null,
        uploadDocumentPanel: !!questionItem?.submittedDocument?.documentId,
        possibleAnswers: questionItem.possibleAnswers.filter((item) =>
          [AnswerType.Yes, AnswerType.No].includes(item.id)
        ),
      }));

      this.questions = questions.filter(
        (x) => ![Questions.DoesStudentHaveSignificantRisks].includes(x.id)
      );

      this.bottomQuestions = questions.filter((x) =>
        [Questions.DoesStudentHaveSignificantRisks].includes(x.id)
      );
    },
    async getSAEE14BehavioralDocument() {
      const params = {
        studentSieId: +(this.selectedStudent?.studentId ?? this.studentSieId),
        includeDocumentTypeIds: [
          documentTypes.SAEE_14EvaluacionFuncionalConducta,
        ],
      };
      const {
        data: studentSAEE14Attachments,
      } = await studentService.getDocumentAttachments(params);
      this.documentSAEE14 = studentSAEE14Attachments.pop();
    },
    async uploadSAEE14BehavioralDocument() {
      const data = {
        id: +this.selectedStudent.studentId,
        itemAttachmentId: this.documentSAEE14?.itemAttachmentId || null,
        documentTypeId: documentTypes.SAEE_14EvaluacionFuncionalConducta,
        file: this.documentSAEE14Model,
        comment:
          'Documento SAEE-14 Evaluación Funcional de la Conducta desde Asistentes para cuidados de salud - Apoyo conductual a la necesidad de salud',
        date: new Date(),
      };

      const formData = objectToFormData(data, { indices: true });
      try {
        await studentService.uploadDocument(formData);
        await this.getSAEE14BehavioralDocument();
        this.ShowToast(
          'Proceso Exitoso.',
          'El documento ha sido guardado exitosamente.',
          'success'
        );
      } catch (err) {
        this.ShowToast('', err.message || err.response.data.message, 'error');
      }
    },
    updateSelectedAnswer(questionId, answerId) {
      let currentQuestion = this.questions.find((x) => x.id === questionId);

      if (!currentQuestion?.id) {
        currentQuestion = this.bottomQuestions.find((x) => x.id === questionId);
        if (!currentQuestion?.id) return;
      }

      currentQuestion.submittedAnswerTypeId = answerId;

      if (this.studentInterventionPlanHasValue(currentQuestion)) {
        this.questions = this.questions.map((questionItem) => {
          if (
            (questionItem.id !== Questions.DoesStudentHaveInterventionPlan &&
              !questionItem.submittedId) ||
            questionItem.id === Questions.WereStudentConductsAnalyzedAfterFBA
          ) {
            questionItem.submittedAnswerTypeId = 0;
          }
          return questionItem;
        });
      }
      if (!this.studentHasReferralForSocioEmotionalAssistant(currentQuestion)) {
        currentQuestion.documentModel = null;
      }
    },
    onAddClickHandler(questionItem) {
      const currentQuestion = this.questions.find(
        (x) => x.id === questionItem.id
      );

      if (currentQuestion) {
        currentQuestion.uploadDocumentPanel = true;
      }
    },
    onUploadFile(questionId, value) {
      const questionItem = this.questions.find((x) => x.id === questionId);
      questionItem.documentModel = value;
      questionItem.submittedDocument = null;
    },
    async onUploadSAEE14File(value) {
      if (!value) return;

      this.documentSAEE14Model = value;

      await this.uploadSAEE14BehavioralDocument();
    },
    async onDownloadDocument(documentId) {
      await documentsService.downloadFromServer(documentId);
    },
    async onSaveQuestions() {
      this.resetHiddenQuestionsBeforeSave();

      const payload = [...this.questionsList, ...this.bottomQuestions].map(
        (questionItem) => ({
          id: questionItem.submittedId,
          peiId: this.selectedPeiId,
          questionServiceNeedId: questionItem.id,
          answerTypeId: questionItem.submittedAnswerTypeId,
          document: questionItem.documentModel,
          comment: null,
        })
      );

      const firstWayToJustify =
        this.questionsList.some((questionItem) =>
          this.studentInterventionPlanHasValue(questionItem)
        ) &&
        this.questionsList.some((questionItem) =>
          this.studentConductWereAnalizedBeforeFBA(questionItem)
        ) &&
        this.inappropriateConductAlternative ===
          StudentBehaviourAlternatives.Destructive;

      const secondWayToJustify =
        this.questionsList.some((questionItem) =>
          this.studentInterventionPlanHasValue(questionItem)
        ) &&
        this.questionsList.some((questionItem) =>
          this.studentHasReferralForSocioEmotionalAssistant(questionItem)
        ) &&
        this.questionsList.some((questionItem) =>
          this.studentConductWereAnalizedBeforeFBA(questionItem)
        ) &&
        this.inappropriateConductAlternative ===
          StudentBehaviourAlternatives.Destructive;

      const justified = firstWayToJustify || secondWayToJustify;

      this.$emit('save-questions', {
        answers: payload,
        isJustified: justified,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';

.question-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px 36px;
}
.question-item {
  gap: 30px;
}
.question-box-content {
  border-bottom: solid 1px map-get($colors, 'gray-5');
}
.custom-radio-container {
  padding-top: 15px;
}
.custom-radio-button {
  width: 60px;
  height: 60px;
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.custom-radio-button,
.radio-label {
  cursor: pointer;
}
.show-check {
  opacity: 1 !important;
}
.step-label {
  display: block;
  font-family: Lato;
  font-weight: 500;
  line-height: 160%;
}

.cr.rounded-circle.cr-inactive {
  border-color: map-get($colors, 'gray-8');
}
.cr.rounded-circle.cr-active {
  border-color: map-get($colors, 'white');
}
.cr-error {
  border-color: map-get($colors, 'red');
}
.box-icon-button {
  padding: 27px 174px;
}
.field-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.07px;
}
.field-value {
  font-size: 16px;
  font-weight: 500;
  line-height: 140%;
}
/************* custom radio *****************/
.checkbox label:after,
.radio label:after {
  content: '';
  display: table;
  clear: both;
}

.checkbox .cr,
.radio .cr {
  position: relative;
  display: inline-block;
  border: 2px solid map-get($colors, 'white');
  width: 1.2em;
  height: 1.2em;
  margin-top: 4px;
}

.checkbox .cr .cr-icon,
.radio .cr .cr-icon {
  position: absolute;
  font-size: 0.6em;
  line-height: 0;
  top: 50%;
  left: 20%;
}

.cr-icon {
  margin-top: 0.04em;
}

.checkbox label input[type='checkbox'],
.radio label input[type='radio'] {
  display: none;
}

.checkbox label input[type='checkbox'] + .cr > .cr-icon,
.radio label input[type='radio'] + .cr > .cr-icon {
  opacity: 0;
}

.checkbox label input[type='checkbox']:checked + .cr > .cr-icon,
.radio label input[type='radio']:checked + .cr > .cr-icon {
  opacity: 1;
}

.checkbox label input[type='checkbox']:disabled + .cr,
.radio label input[type='radio']:disabled + .cr {
  opacity: 0.5;
}
/************* end of custom radio *****************/

.col:nth-child(4) {
  order: 0; /* Esta propiedad coloca el cuarto elemento antes del primer elemento visualmente */
  flex-basis: 100%; /* Esto hace que ocupe toda la línea */
}
</style>
